import { CodeOutlined, FormOutlined, MoreOutlined } from "@ant-design/icons";
import { Badge, Button, Card, Dropdown, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import type { FC } from "react";
import { Link } from "react-router";
import type { Environment, Project } from "../../../types";
import { ENV_MENU_ITEMS, PROJECT_MENU_ITEMS, envBadgeColor } from "../constants";

interface ProjectCardProps {
  project: Project;
  isMobile: boolean;
  onProjectMenuClick: (project: Project, key: string) => void;
  onEnvMenuClick: (projectId: string, env: Environment, key: string) => void;
  onAddEnvironment: (projectId: string) => void;
}

export const ProjectCard: FC<ProjectCardProps> = ({
  project,
  isMobile,
  onProjectMenuClick,
  onEnvMenuClick,
  onAddEnvironment,
}) => {
  return (
    <Card
      styles={{
        header: isMobile
          ? { padding: "12px 16px", flexWrap: "wrap", height: "auto", rowGap: 8 }
          : { padding: "12px 24px" },
        body: isMobile ? { padding: "12px 16px" } : { padding: "16px 24px" },
      }}
      title={
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          <Space size="small" wrap align="center">
            <span style={{ fontSize: 16, fontWeight: 600 }}>{project.name}</span>
            <Tag color={project.kind === "library" ? "purple" : "blue"}>
              {project.kind === "library" ? "Library" : "Site"}
            </Tag>
          </Space>
          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            Created {dayjs(project.createdAt).format("MMM D, YYYY")}
          </div>
        </div>
      }
      extra={
        <Dropdown
          trigger={["click"]}
          menu={{
            items: PROJECT_MENU_ITEMS,
            onClick: ({ key }) => onProjectMenuClick(project, key),
          }}
        >
          <Button type="text" icon={<MoreOutlined />} aria-label="Project actions" />
        </Dropdown>
      }
      style={{ marginBottom: 16 }}
      actions={[
        <Link key="snippets" to={`/snippets?projects=${encodeURIComponent(project.id)}`}>
          <Button type="text" icon={<CodeOutlined />}>
            View snippets
          </Button>
        </Link>,
        // ACF objects can be shared cross-project via a `library` too (see
        // obsidian/Product/ACF Sync.md), same as snippets.
        <Link key="acf" to={`/acf?projects=${encodeURIComponent(project.id)}`}>
          <Button type="text" icon={<FormOutlined />}>
            View ACF
          </Button>
        </Link>,
      ]}
    >
      {project.kind === "library" && (
        <div style={{ textAlign: "center", padding: "24px 0", color: "#888" }}>
          <p style={{ margin: 0 }}>
            Libraries have no environments of their own, they only publish snippets for other
            projects to deploy.
          </p>
        </div>
      )}
      {project.kind !== "library" && (!project.environments || project.environments.length === 0) && (
        <div style={{ textAlign: "center", padding: "24px 0", color: "#888" }}>
          <p style={{ marginBottom: 16 }}>No environments yet</p>
          <Button type="primary" onClick={() => onAddEnvironment(project.id)}>
            Add first environment
          </Button>
        </div>
      )}
      {project.kind !== "library" && project.environments && project.environments.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {project.environments.map((env) => (
            <Card
              key={env.id}
              size="small"
              style={{
                marginBottom: 8,
                borderLeft: `3px solid ${envBadgeColor(env.name)}`,
              }}
              styles={{
                header: { padding: "0 8px 0 16px", minHeight: 40, border: "none" },
                body: { padding: "0 16px 8px" },
              }}
              title={
                <Space size="small" align="baseline" wrap>
                  <Badge color={envBadgeColor(env.name)} />
                  <Typography.Text strong style={{ color: envBadgeColor(env.name) }}>
                    {env.name}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: "normal" }}>
                    {dayjs(env.createdAt).format("MMM D, YYYY")}
                  </Typography.Text>
                </Space>
              }
              extra={
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: ENV_MENU_ITEMS,
                    onClick: ({ key }) => onEnvMenuClick(project.id, env, key),
                  }}
                >
                  <Button type="text" size="small" icon={<MoreOutlined />} aria-label="Environment actions" />
                </Dropdown>
              }
            >
              <Typography.Text type="secondary" style={{ fontSize: 13, wordBreak: "break-all" }}>
                {env.url}
              </Typography.Text>
            </Card>
          ))}
          <Button
            type="dashed"
            block
            onClick={() => onAddEnvironment(project.id)}
            style={{ marginTop: 8 }}
          >
            Add environment
          </Button>
        </div>
      )}
    </Card>
  );
};
